// SPDX-License-Identifier: LicenseRef-VicZhuProprietary
// SPDX-FileCopyrightText: 2026 Vic Zhu <viczhu2014@gmail.com>

import { useQueries } from '@tanstack/react-query'
import { useMemo } from 'react'
import { useDataProvider } from 'react-admin'
import {
  buildDeviceWearRows,
  type DeviceReadingBounds,
  type DeviceWearRow,
} from '../utils/deviceWearPeriods'
import { usePetDevicesWithHistory } from './usePetDevicesWithHistory'

/**
 * Every device the pet has worn, with its first/last reading (vic/vetpo#56).
 *
 * Reuses the devices list every chart already fetches (usePetDevicesWithHistory,
 * historical sensors included) and asks the existing /metrics endpoint for ONE
 * row per end of each device's data: `device=<id>`, sorted by timestamp,
 * perPage 1, once ASC and once DESC. That is exact (not the activation time,
 * not a capped window) and needs no new endpoint. It is 2 tiny requests per
 * device, so callers pass `enabled` only while the export dialog is open.
 */
export function useDeviceWearPeriods(
  petId: string | number | null | undefined,
  enabled: boolean,
): { rows: DeviceWearRow[]; isLoading: boolean; error: unknown } {
  const dataProvider = useDataProvider()
  const {
    data: devices,
    isLoading: devicesLoading,
    error: devicesError,
  } = usePetDevicesWithHistory(enabled && petId != null ? petId : null)

  const edgeQuery = (id: string | number, order: 'ASC' | 'DESC') => ({
    queryKey: ['device-wear-edge', String(id), order],
    queryFn: async (): Promise<Date | null> => {
      const { data } = await dataProvider.getList('metrics', {
        // Explicit depth (L-089): REST default 2 embeds every relation per row; only ids are read.
        meta: { params: { depth: 0 } },
        filter: { device: id },
        sort: { field: 'timestamp', order },
        pagination: { page: 1, perPage: 1 },
      })
      const ts = data?.[0]?.timestamp
      return typeof ts === 'number' && Number.isFinite(ts) ? new Date(ts * 1000) : null
    },
    enabled,
    staleTime: 60_000,
    // No per-query `retry` override: the app-wide policy (utils/queryRetry —
    // 429 retried 3x with 2s/8s/30s backoff, other failures once) applies.
    // `retry: false` turned one transient 429 into "Could not load the sensor
    // list" and a report button stuck on "Checking available data…"
    // (reproduced 4/12 on dev, 2026-09-29).
  })

  const results = useQueries({
    queries: devices.flatMap((d) => [edgeQuery(d.id, 'ASC'), edgeQuery(d.id, 'DESC')]),
  })

  const edgesLoading = results.some((r) => r.isPending && r.fetchStatus !== 'idle')
  const edgesError = results.find((r) => r.error)?.error
  // Stable identity across renders: rebuild only when an edge value changes.
  const edgeKey = results.map((r) => (r.data instanceof Date ? r.data.getTime() : 'x')).join('|')

  // biome-ignore lint/correctness/useExhaustiveDependencies: results is tracked through edgeKey — useQueries returns a new array identity every render
  const rows = useMemo(() => {
    const bounds = new Map<string, DeviceReadingBounds>()
    devices.forEach((d, i) => {
      const first = results[2 * i]?.data ?? null
      const last = results[2 * i + 1]?.data ?? null
      bounds.set(String(d.id), { first, last })
    })
    return buildDeviceWearRows(devices, bounds)
  }, [devices, edgeKey])

  return {
    rows,
    isLoading: enabled && (devicesLoading || edgesLoading),
    error: devicesError ?? edgesError ?? null,
  }
}
